<script setup lang="ts">
/**
 * PButton — bouton du design system PreLLM.
 *
 * Variants :
 *  - primary  : action principale (bg ink, foreground surface). CTA création, save.
 *  - secondary: action neutre (bordure 1px line). Annuler, options.
 *  - ghost    : action discrète (transparent, text muted). Toolbars, logout.
 *  - danger   : action destructrice (bg brick). Delete, revoke key.
 *  - accent   : action olive (rare, pour "Activer IA" et autres CTA secondaires).
 *
 * Tailles : sm (compact pill, dans cards/tables), md (défaut), lg (CTA full-width).
 *
 * Le slot `icon` accueille un PIcon (ou SVG inline) à gauche du label.
 */
interface Props {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent'
    size?: 'sm' | 'md' | 'lg'
    loading?: boolean
    disabled?: boolean
    type?: 'button' | 'submit' | 'reset'
}

withDefaults(defineProps<Props>(), {
    variant: 'primary',
    size: 'md',
    type: 'button',
    loading: false,
    disabled: false,
})

defineEmits<{
    (e: 'click', event: MouseEvent): void
}>()
</script>

<template>
    <button
        :type="type"
        :class="[
            'p-button',
            `p-button--${variant}`,
            `p-button--${size}`,
            { 'p-button--loading': loading },
        ]"
        :disabled="disabled || loading"
        :aria-busy="loading ? 'true' : 'false'"
        @click="$emit('click', $event)"
    >
        <span v-if="loading" class="p-button__spinner p-animate-spin" aria-hidden="true" />
        <slot v-else name="icon" />
        <slot />
    </button>
</template>

<style scoped>
.p-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--p-space-2);
    border: 1px solid transparent;
    border-radius: var(--p-radius-sm);
    font-family: var(--p-font-sans);
    font-weight: var(--p-font-weight-medium);
    cursor: pointer;
    white-space: nowrap;
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        border-color var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}

.p-button:disabled,
.p-button--loading {
    cursor: not-allowed;
    opacity: 0.5;
}

/* === Tailles === */
.p-button--sm {
    padding: 4px 10px;
    font-size: var(--p-text-xs);
    min-height: 24px;
}
.p-button--md {
    padding: 8px 14px;
    font-size: var(--p-text-sm);
    min-height: 32px;
}
.p-button--lg {
    padding: 10px 18px;
    font-size: var(--p-text-base);
    min-height: 40px;
}

/* === Variants === */
.p-button--primary {
    background: var(--p-color-ink);
    color: var(--p-color-surface);
}
.p-button--primary:hover:not(:disabled) {
    background: var(--p-color-ink-2);
}
.p-button--primary:active:not(:disabled) {
    background: var(--p-color-ink-3);
}

.p-button--secondary {
    background: var(--p-color-surface);
    color: var(--p-color-ink-4);
    border-color: var(--p-color-line);
}
.p-button--secondary:hover:not(:disabled) {
    color: var(--p-color-ink);
    border-color: var(--p-color-ink-6);
}

.p-button--ghost {
    background: transparent;
    color: var(--p-color-ink-4);
}
.p-button--ghost:hover:not(:disabled) {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
}

.p-button--danger {
    background: var(--p-color-brick);
    color: var(--p-color-surface);
}
.p-button--danger:hover:not(:disabled) {
    background: color-mix(in srgb, var(--p-color-brick) 90%, black);
}

.p-button--accent {
    background: var(--p-color-olive-bg);
    color: var(--p-color-olive);
    border-color: var(--p-color-olive-soft);
}
.p-button--accent:hover:not(:disabled) {
    background: var(--p-color-olive-soft);
    border-color: var(--p-color-olive);
}

/* Spinner — rotation continue, taille relative au font-size. */
.p-button__spinner {
    display: inline-block;
    width: 0.9em;
    height: 0.9em;
    border: 1.5px solid currentColor;
    border-top-color: transparent;
    border-radius: var(--p-radius-full);
}
</style>
